import type { HexGrid, HexCoord, CascadeStep } from "../types.ts";
import { hexToPixel, coordToKey } from "../engine/hex.ts";
import { BACKGROUND_COLOR, CELL_BORDER_COLOR, HOVER_BORDER_COLOR, getCellColor } from "./colors.ts";

export interface HexRenderState {
  hoveredHex: HexCoord | null;
  overflowHighlight?: CascadeStep | null;
}

export function computeHexLayout(
  grid: HexGrid,
  width: number,
  height: number
) {
  // Calculate hex size to fit the grid
  const margin = 40;
  const maxW = (width - margin * 2) / ((grid.radius * 2 + 1) * 1.8);
  const maxH = (height - margin * 2) / ((grid.radius * 2 + 1) * 1.75);
  const hexSize = Math.floor(Math.min(maxW, maxH, 50));
  const cx = width / 2;
  const cy = height / 2;
  return { hexSize, cx, cy };
}

export function drawHexGrid(
  ctx: CanvasRenderingContext2D,
  grid: HexGrid,
  width: number,
  height: number,
  state: HexRenderState
) {
  const { hexSize, cx, cy } = computeHexLayout(grid, width, height);

  ctx.fillStyle = BACKGROUND_COLOR;
  ctx.fillRect(0, 0, width, height);

  // Build highlight lookup sets
  const overflowKey = state.overflowHighlight
    ? coordToKey(state.overflowHighlight.coord)
    : null;
  const affectedKeys = new Set(
    state.overflowHighlight?.affectedNeighbors.map(coordToKey) ?? []
  );

  for (const cell of grid.cells.values()) {
    const { x, y } = hexToPixel(cell.coord, hexSize, cx, cy);
    const cellKey = coordToKey(cell.coord);
    const isHovered =
      state.hoveredHex &&
      cell.coord.q === state.hoveredHex.q &&
      cell.coord.r === state.hoveredHex.r;
    const isOverflowSource = cellKey === overflowKey;
    const isAffected = affectedKeys.has(cellKey);

    // Hex fill
    ctx.fillStyle = getCellColor(cell.color);
    drawHexPath(ctx, x, y, hexSize - 2);
    ctx.fill();

    // Border + cascade effects
    if (isOverflowSource) {
      // Overflow source: white flash ring with glow
      ctx.save();
      ctx.shadowColor = "#ffffff";
      ctx.shadowBlur = 18;
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 3;
      drawHexPath(ctx, x, y, hexSize - 2);
      ctx.stroke();
      ctx.restore();
    } else if (isAffected) {
      // Affected neighbor: colored glow showing the spread
      const glowColor = getCellColor(cell.color);
      ctx.save();
      ctx.shadowColor = glowColor;
      ctx.shadowBlur = 14;
      ctx.strokeStyle = glowColor;
      ctx.lineWidth = 3;
      drawHexPath(ctx, x, y, hexSize - 2);
      ctx.stroke();
      ctx.restore();
    } else {
      // Normal border
      ctx.strokeStyle = isHovered ? HOVER_BORDER_COLOR : CELL_BORDER_COLOR;
      ctx.lineWidth = isHovered ? 3 : 2;
      drawHexPath(ctx, x, y, hexSize - 2);
      ctx.stroke();
    }

    // Count display
    ctx.fillStyle = isOverflowSource ? "#ffdd57" : "#ffffff";
    ctx.font = `bold ${hexSize * 0.45}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`${cell.count}`, x, y - hexSize * 0.08);

    // Capacity bar (small dots or fraction)
    ctx.font = `${hexSize * 0.25}px sans-serif`;
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.fillText(`/${cell.capacity}`, x, y + hexSize * 0.3);
  }
}

function drawHexPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number
) {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i;
    const hx = x + size * Math.cos(angle);
    const hy = y + size * Math.sin(angle);
    if (i === 0) ctx.moveTo(hx, hy);
    else ctx.lineTo(hx, hy);
  }
  ctx.closePath();
}
